/**
 * Tiny localStorage-backed cache for GET responses. Used as a fallback so
 * the PWA can still render read-only content (announcements, events,
 * sermons, church info, songbook lists, sabbath lessons) when the phone
 * is offline or on a spotty connection.
 *
 * Design notes:
 *  - Opt-in per request via `api.get(url, { cache: true })`. Never touches
 *    writes (POST/PATCH/DELETE) so we don't have to reason about queued
 *    financial or chat side-effects.
 *  - Uses localStorage (small blobs, sync, universally supported). We cap
 *    entries at ~1MB each and cap total cache at ~5MB with LRU eviction.
 *  - Cache key is scoped by URL + serialized params + user token prefix
 *    so two logged-in accounts on the same device don't share state.
 */

const PREFIX = "ch_apicache:";
const MAX_ENTRY_BYTES = 1_000_000; // 1MB per entry
const MAX_TOTAL_BYTES = 5_000_000; // ~5MB total

function scopePrefix() {
  // Include a short hash of the token so caches are per-account. When no
  // token is present (public views) the prefix is just "anon".
  const t = (typeof localStorage !== "undefined" && localStorage.getItem("ch_token")) || "";
  if (!t) return "anon";
  // Cheap non-crypto hash — collisions here are harmless.
  let h = 0;
  for (let i = 0; i < t.length; i++) h = (h * 31 + t.charCodeAt(i)) | 0;
  return `u${(h >>> 0).toString(36)}`;
}

function stableParams(params) {
  if (!params || typeof params !== "object") return "";
  const keys = Object.keys(params).sort();
  return keys.map((k) => `${k}=${String(params[k])}`).join("&");
}

function makeKey(url, params) {
  return `${PREFIX}${scopePrefix()}:${url}?${stableParams(params)}`;
}

function totalBytes() {
  if (typeof localStorage === "undefined") return 0;
  let n = 0;
  for (let i = 0; i < localStorage.length; i++) {
    const k = localStorage.key(i);
    if (k && k.startsWith(PREFIX)) n += (localStorage.getItem(k) || "").length;
  }
  return n;
}

function evictLRU(need) {
  // Drop oldest entries (by stored `at`) until we free `need` bytes.
  const entries = [];
  for (let i = 0; i < localStorage.length; i++) {
    const k = localStorage.key(i);
    if (!k || !k.startsWith(PREFIX)) continue;
    try {
      const parsed = JSON.parse(localStorage.getItem(k));
      entries.push({ k, at: parsed?.at || 0, size: (localStorage.getItem(k) || "").length });
    } catch {
      entries.push({ k, at: 0, size: (localStorage.getItem(k) || "").length });
    }
  }
  entries.sort((a, b) => a.at - b.at);
  let freed = 0;
  for (const e of entries) {
    if (freed >= need) break;
    localStorage.removeItem(e.k);
    freed += e.size;
  }
}

export function readCache(url, params) {
  if (typeof localStorage === "undefined") return null;
  try {
    const raw = localStorage.getItem(makeKey(url, params));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed?.data ?? null;
  } catch {
    return null;
  }
}

export function writeCache(url, params, data) {
  if (typeof localStorage === "undefined") return;
  try {
    const payload = JSON.stringify({ at: Date.now(), data });
    if (payload.length > MAX_ENTRY_BYTES) return; // Too big to bother caching
    const need = payload.length - (totalBytes() < MAX_TOTAL_BYTES ? 0 : payload.length);
    if (totalBytes() + payload.length > MAX_TOTAL_BYTES) {
      evictLRU(Math.max(need, MAX_ENTRY_BYTES));
    }
    localStorage.setItem(makeKey(url, params), payload);
  } catch {
    // Quota exceeded — drop the whole cache and give up quietly.
    try {
      for (let i = localStorage.length - 1; i >= 0; i--) {
        const k = localStorage.key(i);
        if (k && k.startsWith(PREFIX)) localStorage.removeItem(k);
      }
    } catch {
      /* ignore */
    }
  }
}

export function clearApiCache() {
  if (typeof localStorage === "undefined") return;
  try {
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const k = localStorage.key(i);
      if (k && k.startsWith(PREFIX)) localStorage.removeItem(k);
    }
  } catch {
    /* ignore */
  }
}
